import { Button } from '@artosera/ui';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLoaderData, useNavigate } from 'react-router';
import { chargerSession } from '../../auth/session.js';
import { useAuthentification } from '../../auth/useAuthentification.js';
import { AuthCard } from '../AuthCard.js';
import { allerApresConnexion } from '../connexion/connexionFlow.js';
import { CarteDoubleAuthentification } from '../reglages/CarteDoubleAuthentification.js';
import type { DonneesDoubleAuthentification } from './doubleAuthentificationLoader.js';
import styles from './DoubleAuthentificationPage.module.css';

/**
 * `/app/double-authentification` : activer la double authentification hors
 * de toute galerie. C'est là que mène une session confinée à l'enrôlement
 * (`enrolementMfaSeulement`) : la coquille appellerait des routes que l'API
 * lui refuse. L'activation faite, la session est relue et la page demandée
 * s'ouvre.
 */
export function DoubleAuthentificationPage(): JSX.Element {
  const { t } = useTranslation('regie');
  const { session, retour } = useLoaderData<DonneesDoubleAuthentification>();
  const navigate = useNavigate();
  const { deconnecter } = useAuthentification();
  const confinee = session.enrolementMfaSeulement === true;
  // Une session SSO ne modifie pas la sécurité du compte (`session_sso_limitee`) :
  // l'activation passe par une connexion avec mot de passe.
  const parSso = session.compte.connexion?.methode === 'sso';

  return (
    <AuthCard
      title={t('auth.doubleAuthentification.titre')}
      subtitle={t(
        confinee
          ? 'auth.doubleAuthentification.sousTitreObligatoire'
          : 'auth.doubleAuthentification.sousTitre',
      )}
      large
    >
      <div className={styles.page}>
        {parSso ? (
          <p className={styles.note} role="note">
            {t('auth.doubleAuthentification.parSso')}
          </p>
        ) : (
          <CarteDoubleAuthentification
            actif={session.compte.deuxiemeFacteurActive}
            apresChangement={async () => {
              // Libre, la session rouvre aussi le hors-ligne. Un échec ici
              // n'empêche rien : le chargeur du retour la relira.
              await chargerSession(null).catch(() => undefined);
              await allerApresConnexion(navigate, retour);
            }}
          />
        )}
        {confinee || parSso ? (
          <Button variant="ghost" onClick={() => void deconnecter()}>
            {t('auth.doubleAuthentification.deconnecter')}
          </Button>
        ) : (
          <p className={styles.retour}>
            <Link to={retour}>{t('auth.doubleAuthentification.retour')}</Link>
          </p>
        )}
      </div>
    </AuthCard>
  );
}
